import * as React from 'react';

import { parseCalendarDate } from '@rematter/common/dateTime';
import {
  Badge,
  Button,
  DataGridColumn,
  LocalizedDate,
  Menu,
} from '@rematter/ui-web';
import { LucideEllipsis, LucideUser } from 'lucide-react';

import { UserListRecord } from '../../../_graphql/queryUserList';
import { useTranslations } from '../../../i18n/useTranslations';

type UseUserListColumnsArgs = {
  openEditDialog: (user: UserListRecord) => void;
  openManageOrganizationsDialog: (user: UserListRecord) => void;
  openManageAdminAccessDialog: (user: UserListRecord) => void;
};

export const useUserListColumns = ({
  openEditDialog,
  openManageOrganizationsDialog,
  openManageAdminAccessDialog,
}: UseUserListColumnsArgs): DataGridColumn<UserListRecord>[] => {
  const t = useTranslations();

  return React.useMemo(
    () => [
      {
        key: 'avatar',
        header: '',
        width: 50,
        minWidth: 50,
        cell: () => (
          <div className="flex items-center justify-center">
            <div className="flex size-8 items-center justify-center rounded-full bg-neutral/20">
              <LucideUser className="size-4 text-neutral/50" />
            </div>
          </div>
        ),
      },
      {
        key: 'name',
        header: t.userListView.header.name,
        width: 250,
        minWidth: 200,
        cell: (cell) => (
          <div className="font-medium text-neutral/90">
            {cell.row.firstName} {cell.row.lastName}
          </div>
        ),
      },
      {
        key: 'organizations',
        header: t.userListView.header.organization,
        width: 300,
        minWidth: 200,
        cell: (cell) => (
          <div className="flex flex-wrap gap-1">
            {cell.row.organizations && cell.row.organizations.length > 0 ? (
              cell.row.organizations.map(
                (org: { id: string; name: string }) => (
                  <Badge key={org.id} color="primary" size="small">
                    {org.name}
                  </Badge>
                ),
              )
            ) : (
              <span className="text-neutral/50">-</span>
            )}
          </div>
        ),
      },
      {
        key: 'email',
        header: t.userListView.header.email,
        width: 250,
        minWidth: 200,
        cell: (cell) => <div className="text-neutral/60">{cell.row.email}</div>,
      },
      {
        key: 'phoneNumber',
        header: t.userListView.header.phoneNumber,
        width: 150,
        minWidth: 120,
        cell: (cell) => (
          <div className="text-neutral/60">{cell.row.phoneNumber || '-'}</div>
        ),
      },
      {
        key: 'timezone',
        header: t.userListView.header.timezone,
        width: 150,
        minWidth: 120,
        cell: (cell) => (
          <div className="text-neutral/60">{cell.row.timezone || '-'}</div>
        ),
      },
      {
        key: 'status',
        header: t.userListView.header.status,
        width: 120,
        minWidth: 100,
        cell: (cell) => (
          <Badge
            color={cell.row.status === 'ACTIVE' ? 'success' : 'neutral'}
            size="small"
          >
            {cell.row.status === 'ACTIVE'
              ? t.userListView.status.active
              : t.userListView.status.archived}
          </Badge>
        ),
      },
      {
        key: 'createdAt',
        header: t.userListView.header.createdAt,
        width: 150,
        minWidth: 120,
        cell: (cell) => {
          const date = parseCalendarDate(cell.row.createdAt);
          return <LocalizedDate date={date} dateStyle="medium" />;
        },
      },
      {
        key: 'actions',
        header: t.userListView.header.actions,
        width: 50,
        minWidth: 50,
        align: 'center',
        cell: (cell) => (
          <Menu
            trigger={
              <Button size="small" variant="ghost">
                <LucideEllipsis className="size-4" />
              </Button>
            }
            items={[
              {
                label: t.userListView.actions.edit,
                onAction: () => openEditDialog(cell.row),
              },
              {
                label: t.userListView.actions.manageOrganizations,
                onAction: () => openManageOrganizationsDialog(cell.row),
              },
              {
                label: t.userListView.actions.manageAdminAccess,
                onAction: () => openManageAdminAccessDialog(cell.row),
              },
            ]}
          />
        ),
      },
    ],
    [
      t,
      openEditDialog,
      openManageOrganizationsDialog,
      openManageAdminAccessDialog,
    ],
  );
};
